import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ref, get, onValue, onChildAdded } from "firebase/database";
import GoogleMapReact from 'google-map-react';

import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PublicIcon from '@mui/icons-material/Public';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import { Place } from "@material-ui/icons";

import { database } from "../Components/FirebaseConfig";
import UserContext from '../store/UserContext';
import '../App.scss';

import fire from '../Assets/Images/fire.svg'
import ambulance from '../Assets/Images/ambulance.svg'
import police from '../Assets/Images/police.svg'
import shieldCheck from '../Assets/Images/ShieldCheck.svg';
import navigationArrow from '../Assets/Images/NavigationArrow.svg';
import arrowUpRight from '../Assets/Images/ArrowUpRight.svg';


const LocationPin = ({ text }) => {
  return (
    <div className="pin">
      <div className="circle" style={{ animationDelay: "-3s" }}></div>
      <div className="circle" style={{ animationDelay: "-2s" }}></div>
      <div className="circle" style={{ animationDelay: "-1s" }}></div>
      <div className="circle" style={{ animationDelay: " 0s" }}></div>
    </div>
  )
}

const StaticPin = ({ color = '#e74c3d' }) => (
  <div style={{ position: 'absolute', transform: 'translate(-50%, -100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default' }}>
    <svg width="22" height="34" viewBox="0 0 22 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 0C4.925 0 0 4.925 0 11c0 8.25 11 23 11 23S22 19.25 22 11C22 4.925 17.075 0 11 0z" fill={color} />
      <circle cx="11" cy="11" r="4.5" fill="white" />
    </svg>
  </div>
)

const generateInitialsIcon = (name) => {
  let initials = "";

  if (!name || name.trim().toUpperCase() === "N/A" || name.trim() === "") {
    initials = "N/A";
  } else {
    initials = name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  }

  return (
    <div
      style={{
        height: "32px",
        width: "32px",
        minWidth: "32px",
        borderRadius: "100%",
        backgroundColor: "transparent",
        border: "2px solid white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: "12px",
        fontWeight: "bold",
      }}
    >
      {initials}
    </div>
  );
};

const formatLocationTime = (timestamp) => {
  if (timestamp == null) return '—';
  const d = new Date(typeof timestamp === 'number' && timestamp < 1e12 ? timestamp * 1000 : timestamp);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};
const formatLocationDate = (timestamp) => {
  if (timestamp == null) return '—';
  const d = new Date(typeof timestamp === 'number' && timestamp < 1e12 ? timestamp * 1000 : timestamp);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Reads all entries from a tracking node and extracts locations + identity fields
const readTrackingData = async (trackingId) => {
  const snap = await get(ref(database, '/tracking/' + trackingId));
  const data = snap.val();
  if (!data) return null;

  const entries = [];
  let seenUserId = null, seenPhone = null, seenEmail = null, seenName = null;

  Object.keys(data).forEach((key) => {
    const item = data[key];
    const ts = item.timestamp != null
      ? item.timestamp
      : (typeof key === 'string' && /^\d+$/.test(key) ? parseInt(key, 10) : 0);
    if (item.lat != null && item.lng != null) {
      entries.push({ lat: item.lat, lng: item.lng, timestamp: ts });
    }
    if (item.user_id || item.userId) seenUserId = item.user_id || item.userId;
    if (item.phone) seenPhone = item.phone;
    if (item.email) seenEmail = item.email;
    if (item.name) seenName = item.name;
  });

  entries.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  return { entries, seenUserId, seenPhone, seenEmail, seenName };
};

// Calls the API to find a userId from email/phone
const fetchUserId = async (email, phone) => {
  try {
    const res = await fetch('https://api.delivait.com/v3/user/find/id', {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: phone || '', email: email || '' })
    });
    const data = await res.json();
    if (data.status === 'success' && data.data?.userId) return data.data.userId;
  } catch (e) {}
  return null;
};


const Location = () => {

  const userCtx = useContext(UserContext);
  const {
    setAlertCancelled: setContextAlertCancelled,
    setName: setContextName,
    setPhone: setContextPhone,
  } = userCtx;
  const navigate = useNavigate();
  const { trackingId, lat, lng } = useParams();

  const [footerHeight, setFooterHeight] = useState(true);
  const [alertCancelled, setAlertCancelled] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [firstLocation, setFirstLocation] = useState(null);
  const [lastLocation, setLastLocation] = useState(null);
  const [emergencyContact, setEmergencyContact] = useState(null);
  const [contactExpanded, setContactExpanded] = useState(true);
  const [effectiveTrackingId, setEffectiveTrackingId] = useState(trackingId);
  const [center, setCenter] = useState({ lat: parseFloat(lat), lng: parseFloat(lng) });

  // Window resize: restore footer on large screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 800) setFooterHeight(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Main sequential init: tracking data → userId → user node → contacts
  useEffect(() => {
    let cancelled = false;
    let unsubscribeUser = null;
    let unsubscribeAlertSnake = null;
    let unsubscribeContacts = null;

    const applyTrackingLocations = (entries, isNewSession) => {
      if (entries.length === 0) return;
      const first = entries[0];
      const last = entries[entries.length - 1];
      setFirstLocation({ lat: first.lat, lng: first.lng, timestamp: first.timestamp });
      setLastLocation({ lat: last.lat, lng: last.lng, timestamp: last.timestamp });
      if (isNewSession) setCenter({ lat: last.lat, lng: last.lng });
    };

    const init = async () => {
      // Step 1: Read tracking data from the URL's tracking ID
      const tracking = await readTrackingData(trackingId);
      if (cancelled) return;

      if (!tracking) {
        setIsReady(true);
        return;
      }

      applyTrackingLocations(tracking.entries, false);
      if (tracking.seenName) setContextName(tracking.seenName);
      if (tracking.seenPhone) setContextPhone(tracking.seenPhone);

      // Step 2: Resolve userId — it's in the tracking node, or we look it up via API
      let userId = tracking.seenUserId;

      if (!userId && (tracking.seenEmail || tracking.seenPhone)) {
        userId = await fetchUserId(tracking.seenEmail, tracking.seenPhone);
        if (cancelled) return;
      }

      if (!userId) {
        setIsReady(true);
        return;
      }

      // Step 3a: One-time read of trackingId to check if URL is an old link
      const trackingIdSnap = await get(ref(database, '/users/' + userId + '/trackingId'));
      if (cancelled) return;
      const userTrackingId = trackingIdSnap.val();

      if (userTrackingId && userTrackingId !== trackingId) {
        setEffectiveTrackingId(userTrackingId);
        const newTracking = await readTrackingData(userTrackingId);
        if (!cancelled && newTracking) {
          applyTrackingLocations(newTracking.entries, true);
          // Update the URL so the browser bar and any shared link reflect the current session
          const last = newTracking.entries[newTracking.entries.length - 1];
          if (last) {
            navigate(`/${userTrackingId}/${last.lat}/${last.lng}`, { replace: true });
          }
        }
      }

      // Step 3b: Live listener on alertTriggered first, with snake_case fallback
      let firstFire = true;
      let hasCamelAlertField = false;
      const handleAlertSnapshot = (snapshot) => {
        if (cancelled) return;
        const triggered = snapshot.val();
        if (triggered === 'yes' || triggered === true) {
          setAlertCancelled(false);
          setContextAlertCancelled(false);
        } else if (triggered === 'no' || triggered === false) {
          setAlertCancelled(true);
          setContextAlertCancelled(true);
        }
        if (firstFire) {
          firstFire = false;
          setIsReady(true);
        }
      };
      const handleAlertSnakeSnapshot = (snapshot) => {
        if (cancelled || hasCamelAlertField) return;
        handleAlertSnapshot(snapshot);
      };
      const handleAlertError = () => {
        if (!cancelled) setIsReady(true);
      };
      // Prefer camelCase key and fallback to snake_case only if camelCase is absent
      unsubscribeUser = onValue(ref(database, '/users/' + userId + '/alertTriggered'), (snapshot) => {
        if (cancelled) return;
        hasCamelAlertField = snapshot.exists();
        handleAlertSnapshot(snapshot);
        if (!hasCamelAlertField && !unsubscribeAlertSnake) {
          unsubscribeAlertSnake = onValue(
            ref(database, '/users/' + userId + '/alert_triggered'),
            handleAlertSnakeSnapshot,
            handleAlertError
          );
        }
      }, handleAlertError);

      // Step 4: Listen to contacts node
      const contactsRef = ref(database, '/users/' + userId + '/contacts');
      unsubscribeContacts = onValue(contactsRef, (snapshot) => {
        if (cancelled) return;
        const val = snapshot.val();
        if (!val || typeof val !== 'object') {
          return;
        }
        for (const [key, contact] of Object.entries(val)) {
          if (contact && (contact.name || contact.phone)) {
            setEmergencyContact({ name: contact.name || null, phone: contact.phone || key });
            break;
          }
        }
      }, () => {});
    };

    init();

    return () => {
      cancelled = true;
      unsubscribeUser?.();
      unsubscribeAlertSnake?.();
      unsubscribeContacts?.();
    };
  }, [trackingId, setContextAlertCancelled, setContextName, setContextPhone]);

  // Live map center: follow new location pings on the effective tracking ID
  useEffect(() => {
    if (alertCancelled) return;
    const dbRef = ref(database, '/tracking/' + effectiveTrackingId);
    const unsubscribe = onChildAdded(dbRef, (snapshot) => {
      const item = snapshot.val();
      if (item?.lat != null) setCenter({ lat: item.lat, lng: item.lng });
    });
    return () => unsubscribe();
  }, [effectiveTrackingId, alertCancelled]);

  const footerHandler = (e) => {
    e.preventDefault();
    setFooterHeight(!footerHeight);
  }

  const cancelledCenter = firstLocation && lastLocation
    ? { lat: (firstLocation.lat + lastLocation.lat) / 2, lng: (firstLocation.lng + lastLocation.lng) / 2 }
    : firstLocation || lastLocation || center;

  const contactName = emergencyContact?.name || userCtx.name || 'contact';
  const contactPhone = emergencyContact?.phone || userCtx.phone || '';

  if (!isReady) {
    return (
      <div className="location-skeleton">
        <div className="location-skeleton-header">
          <div className="location-skeleton-shimmer" />
          <div className="location-skeleton-logo" />
          <div className="location-skeleton-header-text" />
        </div>
        <div className="location-skeleton-map" />
      </div>
    );
  }

  if (alertCancelled) {
    return (
      <div className="location location-cancelled">
        <div className="map" style={{ position: 'relative' }}>
          <Map
            center={cancelledCenter}
            zoom={14}
            firstLocation={firstLocation}
            lastLocation={lastLocation}
            cancelledMode={true}
          />
          <div className="alert-cancelled-card">
            <div className="alert-cancelled-header">
              <div className="alert-cancelled-icon-wrap">
                <img src={shieldCheck} alt="" className="alert-cancelled-icon" />
              </div>
              <div>
                <h1 className="alert-cancelled-title">Alert Cancelled</h1>
                <p className="alert-cancelled-subtitle">No further action required</p>
              </div>
            </div>
            <p className="alert-cancelled-message">
              {userCtx.name || 'The user'} has cancelled the panic alert. Location tracking has stopped. Please check in to make sure they're okay.
            </p>
            <div className="alert-cancelled-locations">
              <div className="alert-cancelled-location-row">
                <Place style={{ color: '#e74c3d', fontSize: 24 }} />
                <div className="alert-cancelled-location-text">
                  <span className="alert-cancelled-label">Location when alert was triggered</span>
                  <span className="alert-cancelled-place">
                    {firstLocation ? `${firstLocation.lat.toFixed(4)}, ${firstLocation.lng.toFixed(4)}` : '—'}
                  </span>
                  <span className="alert-cancelled-datetime">
                    {firstLocation ? `${formatLocationTime(firstLocation.timestamp)} · ${formatLocationDate(firstLocation.timestamp)}` : '—'}
                  </span>
                </div>
                <a
                  href={firstLocation ? `https://www.google.com/maps?q=${firstLocation.lat},${firstLocation.lng}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="alert-cancelled-nav-icon"
                  aria-label="View on map"
                >
                  <img src={navigationArrow} alt="" className="alert-cancelled-nav-icon-img" />
                </a>
              </div>
              <div className="alert-cancelled-location-row">
                <Place style={{ color: '#2ecc71', fontSize: 24 }} />
                <div className="alert-cancelled-location-text">
                  <span className="alert-cancelled-label">Location when alert was cancelled</span>
                  <span className="alert-cancelled-place">
                    {lastLocation ? `${lastLocation.lat.toFixed(4)}, ${lastLocation.lng.toFixed(4)}` : '—'}
                  </span>
                  <span className="alert-cancelled-datetime">
                    {lastLocation ? `${formatLocationTime(lastLocation.timestamp)} · ${formatLocationDate(lastLocation.timestamp)}` : '—'}
                  </span>
                </div>
                <a
                  href={lastLocation ? `https://www.google.com/maps?q=${lastLocation.lat},${lastLocation.lng}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="alert-cancelled-nav-icon"
                  aria-label="View on map"
                >
                  <img src={navigationArrow} alt="" className="alert-cancelled-nav-icon-img" />
                </a>
              </div>
            </div>
            <div className="alert-cancelled-contact">
              <button
                type="button"
                className="alert-cancelled-contact-heading"
                onClick={() => setContactExpanded((v) => !v)}
                aria-expanded={contactExpanded}
              >
                <span>EMERGENCY CONTACT</span>
                {contactExpanded ? (
                  <ExpandLessRoundedIcon className="alert-cancelled-contact-chevron" />
                ) : (
                  <ExpandMoreRoundedIcon className="alert-cancelled-contact-chevron" />
                )}
              </button>
              <div className={`alert-cancelled-contact-body ${contactExpanded ? 'is-expanded' : ''}`}>
                <a href={`tel:${contactPhone}`} className="alert-cancelled-call-btn">
                <div className="alert-cancelled-call-icon-wrap">
                  <CallOutlinedIcon className="alert-cancelled-call-icon" />
                </div>
                <div className="alert-cancelled-call-text">
                  <span className="alert-cancelled-call-label">Call {contactName}</span>
                  <span className="alert-cancelled-phone">{contactPhone || '—'}</span>
                </div>
                <img src={arrowUpRight} alt="" className="alert-cancelled-call-external" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="location">
      <div className="map">
        {center.lat ? <Map center={center} zoom={15} /> : <div>Loading</div>}
      </div>

      <div className="footer-container" style={footerHeight ? { height: "52px" } :  { height: "250px" }} >
        <div className="location-footer">
          <button className="show-footer" onClick={footerHandler}>{!footerHeight ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}</button>
          <div className="details-row">
            <div className="single-detail panic-name">
              <div className="detail-title-row">
                {generateInitialsIcon(userCtx.name)}<span className="title">{userCtx.name}</span>
              </div>
              <span className="phone-number">
                <a href={`tel:${userCtx.phone}`}>{userCtx.phone}</a>
              </span>
            </div>

            <div className="single-detail">
              <div className="detail-title-row">
                <img src={police} alt="test" /><span className="service-title">Police</span>
              </div>
              <span className="phone-number">
                <a href="tel:9623432186">9623432186</a>
              </span>
            </div>

            <div className="single-detail">
              <div className="detail-title-row">
                <img src={ambulance} alt="test" /><span className="service-title">Ambulance</span>
              </div>
              <span className="phone-number">
                <a href="tel:110177">110177</a>
              </span>
            </div>

            <div className="single-detail">
              <div className="detail-title-row">
                <img src={fire} alt="test" /><span className="service-title">Fire Department</span>
              </div>
              <span className="phone-number">
                <a href="tel:11022">11022</a>
              </span>
            </div>

            <div className="single-detail">
              <div className="detail-title-row">
                <PublicIcon className="detail-icon" /><span className="service-title">International Emergency</span>
              </div>
              <span className="phone-number">
                <a href="tel:112">112</a>
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

export default Location;


const MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const MapLoadError = () => (
  <div className="map-load-error" style={{
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f5f5dc', padding: 24, textAlign: 'center', flexDirection: 'column', gap: 12, boxSizing: 'border-box'
  }}>
    <strong>Map could not load</strong>
    <p style={{ margin: 0, fontSize: 14, maxWidth: 420 }}>
      Open DevTools → <strong>Network</strong> tab and find the request to <code>maps.googleapis.com/maps/api/js</code>.
    </p>
    <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13, textAlign: 'left' }}>
      <li>If status is <strong>403</strong>: enable &quot;Maps JavaScript API&quot;, enable billing, and add your dev URL (e.g. <code>http://localhost:PORT/*</code>) to your API key&apos;s HTTP referrer restrictions.</li>
      <li>If status is <strong>200</strong> but tiles are gray: check the response body for error messages (e.g. RefererNotAllowedMapError).</li>
    </ul>
  </div>
);

const Map = ({ center, zoom, cancelledMode, firstLocation, lastLocation }) => {
  const [mapLoadError, setMapLoadError] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapsApi, setMapsApi] = useState(null);
  const [mapType, setMapType] = useState('roadmap');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapContainerRef = useRef(null);

  const handleApiLoaded = ({ map, maps }) => {
    if (!map || !maps) {
      setMapLoadError(true);
      return;
    }
    setMapInstance(map);
    setMapsApi(maps);
  };

  useEffect(() => {
    if (!mapInstance || !mapsApi || !cancelledMode || !firstLocation || !lastLocation) return;
    const bounds = new mapsApi.LatLngBounds();
    bounds.extend(new mapsApi.LatLng(firstLocation.lat, firstLocation.lng));
    bounds.extend(new mapsApi.LatLng(lastLocation.lat, lastLocation.lng));
    const isSamePoint = firstLocation.lat === lastLocation.lat && firstLocation.lng === lastLocation.lng;
    if (isSamePoint) {
      mapInstance.setCenter({ lat: firstLocation.lat, lng: firstLocation.lng });
      mapInstance.setZoom(15);
    } else {
      mapInstance.fitBounds(bounds, { top: 60, bottom: 320, left: 50, right: 50 });
    }
  }, [mapInstance, mapsApi, cancelledMode, firstLocation, lastLocation]);

  const setMapTypeId = (type) => {
    if (!mapInstance || !mapsApi) return;
    const id = type === 'satellite' ? mapsApi.MapTypeId.SATELLITE : mapsApi.MapTypeId.ROADMAP;
    mapInstance.setMapTypeId(id);
    setMapType(type);
  };

  const toggleFullscreen = () => {
    if (!mapContainerRef.current) return;
    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const getMapOptions = (maps) => ({
    mapTypeControl: false,
    mapTypeId: mapType === 'satellite' ? maps.MapTypeId.SATELLITE : maps.MapTypeId.ROADMAP,
  });

  return (
    <div ref={mapContainerRef} className="map-item map-toolbar-container" style={{ position: 'relative' }}>
      {mapLoadError && <MapLoadError />}
      <div className="map-toolbar">
        <div className="map-toolbar-group">
          <button
            type="button"
            className={`map-toolbar-btn ${mapType === 'roadmap' ? 'active' : ''}`}
            onClick={() => setMapTypeId('roadmap')}
            aria-label="Street view"
          >
            Street
          </button>
          <button
            type="button"
            className={`map-toolbar-btn ${mapType === 'satellite' ? 'active' : ''}`}
            onClick={() => setMapTypeId('satellite')}
            aria-label="Satellite view"
          >
            Satellite
          </button>
        </div>
        <button
          type="button"
          className="map-toolbar-btn map-toolbar-maximize"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Maximize'}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </button>
      </div>
      <GoogleMapReact
        options={getMapOptions}
        bootstrapURLKeys={{ key: MAP_KEY, authReferrerPolicy: 'origin' }}
        center={center}
        defaultZoom={zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
      >
        {!cancelledMode
          ? <LocationPin lat={center.lat} lng={center.lng} />
          : null}
        {cancelledMode && firstLocation
          ? <StaticPin lat={firstLocation.lat} lng={firstLocation.lng} color="#e74c3d" />
          : null}
        {cancelledMode && lastLocation
          ? <StaticPin lat={lastLocation.lat} lng={lastLocation.lng} color="#2ecc71" />
          : null}
      </GoogleMapReact>
    </div>
  );
};
